import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppContent, ContentRow } from "../../../styled/components";
import {
  getCompanies,
  getContact,
  getCountries,
  getLanguages,
  getTags,
  getTitles,
  getUsers,
  saveContact,
  updateContact,
} from "../api-service";
import { defaultAddress, defaultFormData } from "../util/contact.constants";
import {
  mapCreateRequestData,
  mapEditRequestData,
  prepareContactFormData,
} from "../util/form-functions.util";
import MultipleSelectChip from "../util/multiselect-chip.component";
import AddressCard from "./address-card";
import AddressForm from "./address-form";
import { ContactContainer, ContentRowHeader } from "./contact-form.styles";
import ContactTitle from "./contact-title";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isAddressComplete(addr) {
  return !!(
    addr &&
    addr.streetAddress1?.trim() &&
    addr.city?.trim() &&
    addr.postalCode?.trim() &&
    addr.countryId
  );
}

function validateContactForm(data) {
  if (!data.firstName?.trim()) return false;
  if (data.type === "individual" && !data.lastName?.trim()) return false;
  if (!data.email?.trim() || !EMAIL_PATTERN.test(data.email.trim()))
    return false;
  if (!data.lang) return false;
  if (data.type === "company") {
    if (!Array.isArray(data.addresses) || data.addresses.length === 0)
      return false;
  }
  const addresses = Array.isArray(data.addresses) ? data.addresses : [];
  if (addresses.length > 0 && !addresses.every(isAddressComplete)) return false;
  return true;
}

function ContactForm({ contactId, onCancel, onSaved }) {
  const [formData, setFormData] = useState(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [titles, setTitles] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(null);
  const companyAddressCache = useRef({});

  const isEditMode = contactId !== null && contactId !== "new";
  // Form is always editable now; show-mode lives in <ContactShow/>.
  const isReadOnly = false;
  const isIndividualWithCompany =
    formData.type === "individual" && !!formData.companyId;
  const isFormValid = useMemo(() => validateContactForm(formData), [formData]);
  const shouldDisableIndividualRadio = formData.customerOrVendor === "landlord";
  const displayOwnAddresses = isIndividualWithCompany ? "none" : "";

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [t, u, c, l, ti, co] = await Promise.all([
          getTags(),
          getUsers(),
          getCompanies(),
          getLanguages(),
          getTitles(),
          getCountries(),
        ]);

        setTags(Array.isArray(t) ? t : []);
        setUsers(Array.isArray(u) ? u : []);
        setCompanies(Array.isArray(c) ? c : []);
        setLanguages(Array.isArray(l) ? l : []);
        setTitles(Array.isArray(ti) ? ti : []);
        setCountries(Array.isArray(co) ? co : []);

        if (isEditMode) {
          const contactData = await getContact(contactId);
          if (!contactData) {
            throw new Error("Contact not found");
          }
          const prepared = prepareContactFormData(contactData);
          setFormData(prepared);
          // Fallback: si es un individuo con empresa pero la respuesta no trae
          // las direcciones de la empresa, las cargamos aparte para mostrarlas.
          const belongsToCompany =
            prepared.type === "individual" && !!prepared.companyId;
          if (
            belongsToCompany &&
            prepared.parentCompanyAddresses.length === 0
          ) {
            loadCompanyAddresses(prepared.companyId);
          }
        }
      } catch (err) {
        setError("Error loading form data");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [contactId, isEditMode]);

  const handleMainFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Carga las direcciones de la empresa seleccionada para mostrarlas en modo
  // lectura, igual que en la vista de contacto. Usa una caché en memoria para
  // evitar consultar la misma empresa repetidamente.
  const loadCompanyAddresses = async (companyId) => {
    const cached = companyAddressCache.current[companyId];
    if (cached) {
      setFormData((prev) =>
        prev.companyId === companyId
          ? { ...prev, parentCompanyAddresses: cached }
          : prev,
      );
      return;
    }
    try {
      const companyData = await getContact(companyId);
      const companyContact = companyData;
      const addresses = companyContact
        ? prepareContactFormData(companyContact).addresses
        : [];
      companyAddressCache.current[companyId] = addresses;
      setFormData((prev) =>
        prev.companyId === companyId
          ? { ...prev, parentCompanyAddresses: addresses }
          : prev,
      );
    } catch (err) {
      setFormData((prev) =>
        prev.companyId === companyId
          ? { ...prev, parentCompanyAddresses: [] }
          : prev,
      );
    }
  };

  const handleAddressDataChange = (index, name, value) => {
    setFormData((prev) => {
      // La facturación se comporta como selección única: al actuar sobre el
      // check de una dirección, esa pasa a ser la única facturable y el resto
      // se desmarca. Así nunca quedan cero ni varias direcciones de facturación.
      if (name === "isBilling") {
        return {
          ...prev,
          addresses: prev.addresses.map((addr, i) => ({
            ...addr,
            isBilling: i === index,
          })),
        };
      }

      const newAddresses = [...prev.addresses];
      newAddresses[index] = {
        ...newAddresses[index],
        [name]: value,
      };
      return { ...prev, addresses: newAddresses };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);

    try {
      if (isEditMode) {
        const response = await updateContact(mapEditRequestData(formData));
        onSaved?.(response ?? { id: contactId });
      } else {
        const response = await saveContact(mapCreateRequestData(formData));
        onSaved?.(response);
      }
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Unknown error";
      setServerError(message);
      const basicInfoTitle = document.getElementById("basic-info-title");
      basicInfoTitle?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleAddAddress = () => {
    let isBilling = false;
    let addressType = "invoice";
    if (formData.addresses.length === 0) {
      isBilling = true;
      addressType = "billing";
    }
    setFormData((prev) => ({
      ...prev,
      addresses: [
        ...prev.addresses,
        {
          ...defaultAddress,
          isBilling,
          addressType,
        },
      ],
    }));
  };

  const handleRemoveAddress = (index) => {
    setFormData((prev) => {
      const remaining = prev.addresses.filter((_, i) => i !== index);
      // Si se elimina la dirección facturable, reasignamos billing a la
      // primera restante para mantener siempre una única dirección de facturación.
      const hasBilling = remaining.some((addr) => addr.isBilling);
      const addresses =
        !hasBilling && remaining.length > 0
          ? remaining.map((addr, i) => ({ ...addr, isBilling: i === 0 }))
          : remaining;
      return { ...prev, addresses };
    });
  };

  const handleContactTypeChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (value === "company") {
      checkAdressesByCompanyType();
    } else {
      checkAdressesByIndividualType();
    }
  };

  const checkAdressesByCompanyType = () => {
    const addresses = formData.addresses || [];
    if (addresses.length === 0) {
      setFormData((prev) => ({
        ...prev,
        addresses: [
          {
            ...defaultAddress,
            addressType: "billing",
            isBilling: true,
          },
        ],
      }));
    }
  };

  function checkAdressesByIndividualType() {
    const hasCompanyAssigned =
      typeof formData.companyId !== "undefined" && formData.companyId !== null;
    const addresses = formData.addresses || [];
    if (hasCompanyAssigned && addresses.length > 0) {
      setFormData((prev) => ({
        ...prev,
        addresses: [],
      }));
    }
  }

  const handleTagsChange = (newTags) => {
    setFormData({
      ...formData,
      tags: newTags,
    });
  };

  const handleCheckboxChange = (field, checked) => {
    if (field === "landlord" && checked) {
      setFormData({
        ...formData,
        customer: true,
        vendor: true,
        landlord: true,
      });
    } else {
      setFormData({
        ...formData,
        [field]: checked,
      });
    }
  };

  const customerVendorOrLandlord = () => {
    if (formData.landlord) {
      return "Landlord";
    }
    if (formData.vendor) {
      return "Vendor";
    }
    return "Customer";
  };

  const displayedName = () => {
    if (formData.type === "company") {
      return formData.firstName;
    } else {
      return `${formData.firstName} ${formData.lastName}`;
    }
  };

  if (loading) {
    return (
      <AppContent>
        <ContentRow>
          <Typography variant="h5" component="h1">
            Loading contact data...
          </Typography>
        </ContentRow>
      </AppContent>
    );
  }

  if (error) {
    return (
      <AppContent>
        <ContentRow>
          <Typography variant="h5" component="h1" color="error">
            {error}
          </Typography>
        </ContentRow>
      </AppContent>
    );
  }

  return (
    <AppContent>
      <ContentRow sx={ContentRowHeader}>
        <Typography variant="h5" component="h1">
          {!isEditMode ? (
            "Create new contact"
          ) : (
            <ContactTitle data={formData} contactId={contactId} />
          )}
        </Typography>

        <div style={{ display: "flex", gap: "8px" }}>
          <Button variant="outlined" color="error" onClick={() => onCancel?.()}>
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={loading || !isFormValid}
            color="primary"
            onClick={handleSubmit}
          >
            {isEditMode ? "Update" : "Save"}
          </Button>
        </div>
      </ContentRow>

      <Box sx={ContactContainer}>
        <Box
          component="form"
          onSubmit={(e) => e.preventDefault()}
          noValidate
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
          }}
        >
          {loading ? (
            <Typography>Loading contact data...</Typography>
          ) : (
            <>
              <Box
                sx={{
                  width: 180,
                  flexShrink: 0,
                  backgroundColor: "#f5f5f5",
                  padding: "24px",
                  display: { xs: "none", md: "flex" },
                  flexDirection: "column",
                  alignItems: "center",
                  borderRight: "1px solid #ddd",
                }}
              >
                <Box
                  sx={{
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    border: "1px solid",
                    borderColor: "action.active",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {formData.type === "company" ? (
                    <BusinessIcon
                      sx={{ fontSize: 96, color: "action.active" }}
                      aria-label="Company contact"
                    />
                  ) : (
                    <PersonIcon
                      sx={{ fontSize: 96, color: "action.active" }}
                      aria-label="Individual contact"
                    />
                  )}
                </Box>

                {/* Nombre/Título */}
                <Typography
                  variant="h6"
                  sx={{ textAlign: "center", width: "100%" }}
                >
                  {displayedName()}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  {formData.function || ""}
                </Typography>

                {/* Botón Vendor/Tipo */}
                <Button variant="outlined" size="small">
                  {" "}
                  {customerVendorOrLandlord()}{" "}
                </Button>
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  padding: { xs: "16px", md: "24px" },
                }}
              >
                <Typography
                  id="basic-info-title"
                  variant="h5"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Basic information
                </Typography>

                {/* Banda de error del backend */}
                {serverError && (
                  <Alert
                    severity="error"
                    variant="filled"
                    onClose={() => setServerError(null)}
                    sx={{ mb: 2, mt: 1 }}
                  >
                    {serverError}
                  </Alert>
                )}

                <RadioGroup
                  row
                  name="type"
                  value={formData.type ?? ""}
                  onChange={handleContactTypeChange}
                  sx={{ display: contactId !== "new" ? "none" : "inline" }}
                >
                  <FormControlLabel
                    value="individual"
                    control={<Radio size="small" />}
                    label="Individual"
                    disabled={isReadOnly || shouldDisableIndividualRadio}
                  />
                  <FormControlLabel
                    value="company"
                    control={<Radio size="small" />}
                    label="Company"
                    disabled={isReadOnly}
                  />
                </RadioGroup>

                <br />

                <FormGroup row sx={{ display: "inline" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.customer || false}
                        onChange={(e) =>
                          handleCheckboxChange("customer", e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    }
                    label="Customer"
                    sx={{
                      display:
                        "inline" /*(contactId !== 'new' && formData.customerOrVendor === 'landlord') ? 'none' : 'inline'*/,
                    }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.vendor || false}
                        onChange={(e) =>
                          handleCheckboxChange("vendor", e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    }
                    label="Vendor"
                    sx={{
                      display:
                        "inline" /*(contactId !== 'new' && formData.customerOrVendor === 'landlord') ? 'none' : 'inline'*/,
                    }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.landlord || false}
                        onChange={(e) =>
                          handleCheckboxChange("landlord", e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    }
                    label="Landlord"
                    sx={{
                      display: formData.type === "company" ? "inline" : "none",
                    }}
                  />
                </FormGroup>

                {/* Fields Title / First Name / Last Name for individual */}
                {formData.type === "individual" && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <Autocomplete
                      name="titleId"
                      disablePortal
                      readOnly={isReadOnly}
                      options={Array.isArray(titles) ? titles : []}
                      value={
                        (titles || []).find(
                          (option) => option.id === formData.titleId,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(event, newValue) => {
                        const newId = newValue ? newValue.id : null;
                        handleMainFieldChange({
                          target: {
                            name: "titleId",
                            value: newId,
                          },
                        });
                      }}
                      sx={{ flexGrow: 2 }}
                      renderInput={(params) => (
                        <TextField
                          margin="normal"
                          variant="outlined"
                          {...params}
                          label="Title"
                        />
                      )}
                    />

                    <TextField
                      autoFocus
                      name="firstName"
                      label="First name"
                      type="text"
                      variant="outlined"
                      value={formData.firstName ?? ""}
                      onChange={handleMainFieldChange}
                      required
                      inputprops={{
                        readOnly: isReadOnly,
                      }}
                      margin="normal"
                      sx={{ mb: 2, flexGrow: 5 }}
                    />
                    <TextField
                      name="lastName"
                      label="Last name"
                      type="text"
                      variant="outlined"
                      inputprops={{
                        readOnly: isReadOnly,
                      }}
                      value={formData.lastName ?? ""}
                      onChange={handleMainFieldChange}
                      required
                      margin="normal"
                      sx={{ mb: 2, flexGrow: 5 }}
                    />
                  </Box>
                )}

                {/* Fields Company Name / Vat Number for company */}
                {formData.type === "company" && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <TextField
                      autoFocus
                      name="firstName"
                      label="Company name"
                      type="text"
                      variant="outlined"
                      value={formData.firstName ?? ""}
                      onChange={handleMainFieldChange}
                      required
                      inputprops={{
                        readOnly: isReadOnly,
                      }}
                      margin="normal"
                      sx={{ mb: 2, flexGrow: 9 }}
                    />
                    <TextField
                      name="vat"
                      label="VAT"
                      type="text"
                      variant="outlined"
                      value={formData.vat ?? ""}
                      onChange={handleMainFieldChange}
                      inputprops={{
                        readOnly: isReadOnly,
                      }}
                      margin="normal"
                      sx={{ mb: 2, flexGrow: 3 }}
                    />
                  </Box>
                )}

                {/* Campo Email / Job position */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <TextField
                    margin="normal"
                    name="email"
                    label="Email"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={formData.email ?? ""}
                    onChange={handleMainFieldChange}
                    required
                    inputprops={{
                      readOnly: isReadOnly,
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    margin="normal"
                    name="function"
                    label="Job position"
                    fullWidth
                    inputprops={{
                      readOnly: isReadOnly,
                    }}
                    value={formData.function ?? ""}
                    onChange={handleMainFieldChange}
                    sx={{
                      display: formData.type === "company" ? "none" : "inline",
                    }}
                  />
                </Box>

                {/* Campo Company / Phone */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <Autocomplete
                    name="companyId"
                    disablePortal
                    readOnly={isReadOnly}
                    sx={{
                      display: formData.type === "company" ? "none" : "inline",
                    }}
                    fullWidth
                    options={Array.isArray(companies) ? companies : []}
                    value={
                      (companies || []).find(
                        (option) => option.id === formData.companyId,
                      ) || null
                    }
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      const newId = newValue ? newValue.id : null;

                      setFormData((prev) => {
                        // Si es un individuo y ahora tiene empresa, vaciamos direcciones
                        const shouldClearAddresses =
                          prev.type === "individual" && newId !== null;

                        return {
                          ...prev,
                          companyId: newId,
                          addresses: shouldClearAddresses ? [] : prev.addresses,
                          // Al deseleccionar la empresa, ocultamos su dirección.
                          parentCompanyAddresses:
                            newId === null ? [] : prev.parentCompanyAddresses,
                        };
                      });

                      // Al seleccionar una empresa, cargamos su dirección en modo lectura.
                      if (newId !== null) {
                        loadCompanyAddresses(newId);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        margin="normal"
                        variant="outlined"
                        fullWidth
                        {...params}
                        label="Company"
                      />
                    )}
                  />

                  <TextField
                    margin="normal"
                    name="phoneNumber"
                    label="Phone number"
                    type="tel"
                    fullWidth
                    inputprops={{
                      readOnly: isReadOnly,
                    }}
                    variant="outlined"
                    value={formData.phoneNumber ?? ""}
                    onChange={handleMainFieldChange}
                    sx={{ mb: 2 }}
                  />
                </Box>

                {/*Campo Language*/}
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <Autocomplete
                    name="lang"
                    disablePortal
                    fullWidth
                    readOnly={isReadOnly}
                    sx={{ mb: 2 }}
                    options={Array.isArray(languages) ? languages : []}
                    value={
                      (languages || []).find(
                        (option) => option.code === formData.lang,
                      ) || null
                    }
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.code === value.code
                    }
                    onChange={(event, newValue) => {
                      const newId = newValue ? newValue.code : null;
                      handleMainFieldChange({
                        target: {
                          name: "lang",
                          value: newId,
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        margin="normal"
                        variant="outlined"
                        fullWidth
                        {...params}
                        required
                        label="Language"
                      />
                    )}
                  />

                  <Autocomplete
                    name="salesPersonId"
                    disablePortal
                    readOnly={isReadOnly}
                    fullWidth
                    sx={{ mb: 2 }}
                    options={Array.isArray(users) ? users : []}
                    value={
                      (users || []).find(
                        (option) => option.id === formData.salesPersonId,
                      ) || null
                    }
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      const newId = newValue ? newValue.id : null;

                      handleMainFieldChange({
                        target: {
                          name: "salesPersonId",
                          value: newId,
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        margin="normal"
                        variant="outlined"
                        fullWidth
                        {...params}
                        label="Sales Person"
                      />
                    )}
                  />
                </Box>

                {/*Campo Tags*/}
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <MultipleSelectChip
                    disablePortal
                    fullWidth
                    sx={{ mb: 2 }}
                    tags={tags}
                    selectedTags={formData.tags}
                    onChange={handleTagsChange}
                    isReadOnly={isReadOnly}
                  ></MultipleSelectChip>
                </Box>

                {/*Campo Internal Notes*/}
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <TextField
                    margin="normal"
                    name="internalNote"
                    label="Internal Notes"
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Internal note..."
                    inputprops={{
                      readOnly: isReadOnly,
                    }}
                    value={formData.internalNote ?? ""}
                    onChange={handleMainFieldChange}
                    sx={{ mb: 2 }}
                  />
                </Box>

                {isIndividualWithCompany ? (
                  <>
                    <Typography variant="h5" sx={{ mt: 4, mb: 1 }}>
                      Company address
                    </Typography>
                    {formData.parentCompanyAddresses.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 1 }}
                      >
                        The associated company has no registered address.
                      </Typography>
                    ) : (
                      formData.parentCompanyAddresses.map((address, index) => (
                        <AddressCard
                          key={`company-${index}`}
                          address={address}
                          countries={countries}
                        />
                      ))
                    )}
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h5"
                      sx={{ mt: 4, mb: 1, display: displayOwnAddresses }}
                    >
                      Addresses
                    </Typography>

                    {formData.addresses.map((address, index) => (
                      <AddressForm
                        key={index}
                        index={index}
                        address={address}
                        onChange={handleAddressDataChange}
                        onRemove={handleRemoveAddress}
                        canRemove={
                          (formData.type === "company" &&
                            formData.addresses.length > 1) ||
                          formData.type === "individual"
                        }
                        isReadOnly={isReadOnly}
                        addressSize={formData.addresses.length}
                        countries={countries}
                      />
                    ))}

                    <Button
                      startIcon={<AddIcon />}
                      onClick={handleAddAddress}
                      variant="outlined"
                      sx={{
                        mt: 2,
                        mb: 2,
                        display: displayOwnAddresses,
                      }}
                      disabled={isReadOnly}
                    >
                      Add Address
                    </Button>
                  </>
                )}

                <button type="submit" style={{ display: "none" }} />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </AppContent>
  );
}

export default ContactForm;
